import React from 'react';

interface StaffFormSkeletonProps {
  /**
   * Title to display for the form
   */
  title?: string;

  /**
   * Whether to include third party status fields
   */
  includeThirdPartyStatus?: boolean;

  /**
   * Whether to include license fields (for transportation staff)
   */
  includeLicenseFields?: boolean;

  /**
   * Whether to include description field (for supporting staff)
   */
  includeDescription?: boolean;

  /**
   * Additional class names to apply to the skeleton container
   */
  className?: string;
}

/**
 * StaffFormSkeleton component
 *
 * This component provides a loading skeleton specifically designed for staff-related forms.
 * It can be customized for different types of staff (teachers, transportation, supporting staff).
 *
 * @example
 * // For teachers
 * <StaffFormSkeleton title="Add New Teacher" />
 *
 * // For transportation staff with license fields
 * <StaffFormSkeleton title="Add New Transportation Staff" includeThirdPartyStatus={true} includeLicenseFields={true} />
 *
 * // For supporting staff with description
 * <StaffFormSkeleton title="Add New Staff Member" includeDescription={true} />
 */
export const StaffFormSkeleton: React.FC<StaffFormSkeletonProps> = ({
  title = 'Add New Staff',
  includeThirdPartyStatus = false,
  includeLicenseFields = false,
  includeDescription = false,
  className = '',
}) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] pb-24 ${className}`}>
      <div className="w-full max-w-[1000px] mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="animate-pulse">
          {/* Header with back button */}
          <div className="flex flex-row justify-start items-center mb-8 gap-4">
            <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
            <div className="h-8 w-56 bg-gray-200 rounded"></div>
          </div>

          {/* Form container */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#E4E8EA] p-4 sm:p-8">
            {/* Profile Image Section */}
            <div className="mb-6 sm:mb-8">
              <div className="h-5 w-32 bg-gray-200 rounded mb-4"></div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gray-200 rounded-xl"></div>
                <div className="h-4 w-48 bg-gray-200 rounded"></div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* Name Fields */}
              <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                <div className="flex-1">
                  <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                  <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                </div>
                <div className="flex-1">
                  <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                  <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                </div>
                <div className="flex-1">
                  <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                  <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                </div>
              </div>

              {/* Employee ID */}
              <div>
                <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
              </div>

              {/* Designation */}
              <div>
                <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
              </div>

              {/* Third Party Status (conditional) */}
              {includeThirdPartyStatus && (
                <div>
                  <div className="h-5 w-36 bg-gray-200 rounded mb-2"></div>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                    <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                </div>
              )}

              {/* License Fields (conditional) */}
              {includeLicenseFields && (
                <>
                  <div>
                    <div className="h-5 w-36 bg-gray-200 rounded mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                  </div>
                  <div>
                    <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                  </div>
                </>
              )}

              {/* Description (conditional) */}
              {includeDescription && (
                <div>
                  <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                  <div className="h-24 bg-gray-200 rounded-xl w-full"></div>
                </div>
              )}

              {/* Email */}
              <div>
                <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                <div className="h-4 w-72 bg-gray-200 rounded mt-2"></div>
              </div>

              {/* Contact */}
              <div>
                <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
              </div>

              {/* Joining Date */}
              <div>
                <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
              </div>

              {/* Emergency Contact Fields */}
              <div className="pt-4 border-t border-gray-100">
                <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>

                <div className="space-y-6">
                  <div>
                    <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                  </div>
                  <div>
                    <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                  </div>
                  <div>
                    <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Placeholder */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
            <div className="max-w-[1000px] mx-auto flex justify-end gap-4">
              <div className="h-10 w-24 bg-gray-200 rounded-xl"></div>
              <div className="h-10 w-24 bg-gray-200 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export specific variations for different staff types
export const TeacherFormSkeleton: React.FC = () => (
  <StaffFormSkeleton title="Add New Teacher" />
);

export const TransportationFormSkeleton: React.FC = () => (
  <StaffFormSkeleton
    title="Add New Transportation Staff"
    includeThirdPartyStatus={true}
    includeLicenseFields={true}
  />
);

export const SupportingStaffFormSkeleton: React.FC = () => (
  <StaffFormSkeleton title="Add New Staff Member" includeDescription={true} />
);
